import { defineStore } from "pinia";
import Vue from "vue";
import MATE_COUNTERS_QUERY from "@/graphql/mateCounters.graphql";
import { useUserStore } from "~/stores/user";
import { requestInFlight, trackRequest } from "~/stores/user/inFlightRequests";

const belongsTo = (counters, me) =>
  !!counters && !!me && String(counters.id) === String(me.id);

const needsCounters = (me) => !!me && !me.isCustomer;

/**
 * The job-feed counters, buy permission and approval and activity flags
 * (`isApproved`, `isInactive`) of the logged-in professional
 * (`graphql/mateCounters.graphql`). The job-feed pages load them in the
 * `mateCounters` middleware, and `TendersHeader.vue` and the settings page
 * load them where they are shown elsewhere; they are not part of `me`.
 */
export const useMateCountersStore = defineStore("mateCountersStore", {
  state: () => ({
    loadedCounters: null,
    pendingQuery: null,
  }),
  getters: {
    /**
     * The counters of the logged-in person, or null until they are loaded.
     * @returns {object | null}
     */
    counters(state) {
      return belongsTo(state.loadedCounters, useUserStore().me)
        ? state.loadedCounters
        : null;
    },
  },
  actions: {
    /**
     * Loads the counters once per person, without sending a second request
     * while one is in flight.
     * Requests in flight, also a refresh of loaded data or one that replaced
     * this call's own request, are waited for, so the call never returns with
     * data that is about to be replaced or without the data a newer request
     * brings.
     */
    async load() {
      if (!needsCounters(useUserStore().me)) {
        return;
      }
      if (
        !this.counters &&
        !requestInFlight(this, "counters", this.pendingQuery)
      ) {
        await this.sendQuery();
      }
      let pending = requestInFlight(this, "counters", this.pendingQuery);
      while (pending) {
        await pending.catch(() => {});
        pending = requestInFlight(this, "counters", this.pendingQuery);
      }
    },

    /**
     * Reloads the counters after an action that changed them. A response that
     * arrives after a newer request was sent is dropped.
     */
    async refresh() {
      if (!needsCounters(useUserStore().me)) {
        return;
      }
      await this.sendQuery();
    },

    /**
     * Reloads the counters only if they are loaded or loading, for callers
     * that changed them.
     */
    async refreshIfLoaded() {
      if (
        this.counters ||
        requestInFlight(this, "counters", this.pendingQuery)
      ) {
        await this.refresh();
      }
    },

    /**
     * Applies the result of a mutation to the loaded counters, over a
     * response that was requested before it.
     * @param {object} values
     */
    setValues(values) {
      const counters = this.counters;
      if (!counters) {
        return;
      }
      this.pendingQuery = null;
      for (const [key, value] of Object.entries(values)) {
        if (!key.startsWith("__")) {
          Vue.set(counters, key, value);
        }
      }
    },

    async sendQuery() {
      const userStore = useUserStore();
      const query = this.$nuxt.$query(
        MATE_COUNTERS_QUERY,
        {},
        { skipErrorPageOnAuthError: true }
      );
      const id = trackRequest(this, "counters", query);
      this.pendingQuery = id;
      try {
        const result = await query;
        if (this.pendingQuery === id && belongsTo(result?.me, userStore.me)) {
          this.loadedCounters = result.me;
        }
      } catch (error) {
        this.$nuxt.$logger.warn("Failed to load the mate counters", error);
      } finally {
        if (this.pendingQuery === id) {
          this.pendingQuery = null;
        }
      }
    },
  },
});
